import PropTypes from 'prop-types';
import React from 'react';

import {
    IconChevronSprite,
} from '$partials/icons/index.js';

import Image from '$partials/media/image.jsx';

import {
    CarouselProvider,
    useCarousel
} from '$contexts/carousel.jsx';

import Carousel, { CarouselSlide } from '$partials/ui/carousel.jsx';

import Button from '$partials/ui/button.jsx';
import { useLocale } from '$contexts/locale.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {{ itemsCollection: any, mainCtaLabel: string, mainCtaLink: string, formatURI: (uri: string) => string }} props
 */
const WalkthroughMobileFooter = ({ itemsCollection, mainCtaLabel, mainCtaLink, formatURI }) => {
    const { currentIndex } = useCarousel();
    const activeItem = itemsCollection?.items?.[currentIndex];
    const ctaLabel = (activeItem?.ctaLabel && activeItem?.ctaLink) ? activeItem.ctaLabel : mainCtaLabel;
    const ctaLink = (activeItem?.ctaLabel && activeItem?.ctaLink) ? activeItem.ctaLink : mainCtaLink;

    return (
        <div className='mt-20 layout-container layout-site-outer-padding items-center flex justify-between'>
            <div className='flex gap-x-8'>
                {[...Array(itemsCollection?.items?.length).keys()].map((_col, i) => (
                    <svg
                        className='inline'
                        key={i}
                        style={{ width: '12px', height: '12px' }}
                        viewBox='0 0 12 12'
                        xmlns='http://www.w3.org/2000/svg'>
                        <circle
                            cx='6'
                            cy='6'
                            fill={currentIndex === i ? '#FC5200' : '#D9D9D9'}
                            r='6' />
                    </svg>
                ))}
            </div>
            {ctaLabel && ctaLink && (
                <Button
                    as='a'
                    className='button button--secondary-alt'
                    href={formatURI(ctaLink)}>
                    <span className='button__label--animate'>
                        <span>{ctaLabel}</span>
                    </span>
                    <span className='button__icon button__icon--animate'>
                        <IconChevronSprite />
                    </span>
                </Button>
            )}
        </div>
    );
};

/**
 * @type {import('$types/components/partials/modules.d.ts').ModuleWalkthrough}
 */

const ModuleWalkthroughMobile = ({
    eyebrow,
    heading,
    itemsCollection,
    itemsVariant,
    bodyCopy,
    mainCtaLabel,
    mainCtaLink,
    isArticlePage,
}) => {
    const { formatURI } = useLocale();

    // Todo: check spacing around text after DN figma updates

    return (
        <div className='block md:hidden bg-white relative overflow-hidden py-40'>
            <div className='layout-container layout-site-outer-padding'>
                <div className='bg-white'>
                    <section className='module-walk-through'>
                        {eyebrow && (
                            <p
                                className='type-style-eyebrow text-text-accent mb-12'>
                                {eyebrow}
                            </p>
                        )}

                        <h2 className={`${isArticlePage ? 'type-style-4' : 'type-style-3-extended'} text-text-primary mb-10`}>
                            {heading}
                        </h2>

                        {bodyCopy && (
                            <p className='type-style-body text-text-secondary'>
                                {bodyCopy}
                            </p>
                        )}

                        {/* Desktop */}
                        <div className='mt-20 w-screen max-w-screen left-1/2 relative -translate-x-1/2'>
                            <CarouselProvider
                                swiperConfig={{
                                    slidesPerView: 1,
                                    spaceBetween: 16,
                                    navigation: true,
                                    pagination: false,
                                    breakpoints: {
                                        768: {
                                            slidesPerView: 1.1,
                                        }
                                    },
                                }}>
                                <div className='layout-container layout-site-outer-padding overflow-visible mt-30'>

                                    <Carousel className='flex swiper-wrapper h-auto w-full relative'>

                                        {itemsCollection?.items?.length > 0 && (
                                            itemsCollection?.items?.map((itm, idx) => {
                                                return (
                                                    <CarouselSlide
                                                        className='flex flex-col relative pb-16 sm:pb-20 mr-16 last:mr-0'
                                                        index={idx}
                                                        key={`${idx}`}>
                                                        <div className='h-full'>
                                                            <div className='bg-surface-raised px-16 py-24 sm:p-16 rounded flex flex-col sm:flex-row gap-24'>
                                                                <div className='sm:w-[45%] flex flex-col gap-y-4'>
                                                                    {itemsVariant === 'Numbered' ?
                                                                        <span className='text-text-accent type-style-5'>
                                                                            {`0${idx + 1}`}
                                                                        </span> :
                                                                        null
                                                                    }
                                                                    <h3 className='type-style-5 text-text-primary'>
                                                                        {itm?.heading}
                                                                    </h3>
                                                                    <p className='type-style-body text-text-secondary'>
                                                                        {itm?.copy}
                                                                    </p>

                                                                </div>
                                                                <div className='sm:w-[55%]'>
                                                                    <div className='aspect-square pointer-events-none select-none'>
                                                                        <figure className='h-full w-full rounded-button overflow-hidden'>
                                                                            {/* To do - check mobile media size - fixed? */}
                                                                            {itm?.media?.contentType?.includes('video/') ?
                                                                                (
                                                                                    <video
                                                                                        autoPlay
                                                                                        loop
                                                                                        muted
                                                                                        playsInline
                                                                                        className='w-full h-full object-cover'
                                                                                        src={itm?.media?.src}
                                                                                        width={'100%'} />
                                                                                ) : (
                                                                                    <Image
                                                                                        {...itm?.media}
                                                                                        className='h-full w-full object-center object-cover select-none pointer-events-none'
                                                                                        containerClassName='w-full h-full relative'
                                                                                        height='100%'
                                                                                        loadWidth={2560}
                                                                                        width='100%' />
                                                                                )
                                                                            }
                                                                        </figure>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </CarouselSlide>
                                                );
                                            })
                                        )}
                                    </Carousel>
                                </div>


                                {/* Button */}
                                <WalkthroughMobileFooter
                                    formatURI={formatURI}
                                    itemsCollection={itemsCollection}
                                    mainCtaLabel={mainCtaLabel}
                                    mainCtaLink={mainCtaLink} />
                            </CarouselProvider>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    eyebrow: PropTypes.string,
    title: PropTypes.string,
    itemsCollection: PropTypes.object,
    itemsVariant: PropTypes.string,
    mainCtaLabel: PropTypes.string,
    mainCtaLink: PropTypes.string,
    standfirst: PropTypes.string,
    parent: PropTypes.string,
};

ModuleWalkthroughMobile.propTypes = propTypes;

export default ModuleWalkthroughMobile;
